import {useCallback, useEffect, useMemo, useState} from "react";
// Aliased: Chart.js exports a Tooltip of its own, registered below.
import {Paper, Tooltip as MuiTooltip, Typography} from "@mui/material";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Grid from "@mui/material/Grid";
import {formatData} from "../../../utils/utils";
import {CategoryScale, Chart as ChartJS, Filler, Legend, LineElement, LinearScale, PointElement, Tooltip,} from 'chart.js';
import {Line} from "react-chartjs-2";
import Box from "@mui/material/Box";
// useStyles for mainHeading: the heading below the tabs has to be the same one widgetsGrid draws above the
// other cards, or this card's title would be a near-match of its neighbours' rather than the same thing.
import {ToggleButton, useStyles} from "../../../utils/uiUtils";
import {fetchIngestedTimelineData} from "../../../utils/dashboardCalls";
import {useDashboardJobScope} from "../../../views/JobContextProvider/useDashboardJobScope";
import {AreaChartSkeleton, TotalsStripSkeleton} from "../graphSkeleton";

// Filler is what paints the area under each line; without it registered the fills are silently dropped
// and the chart renders as bare strokes.
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip, Legend);

/** Palette entry → the same colour at a given opacity, for the area fills under each line. */
const withAlpha = (colour, alpha) => colour.replace(/rgba?\(([^)]+)\)/, (_match, channels) => {
    const [r, g, b] = channels.split(',').map(channel => channel.trim());
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
});

/**
 * A top-to-bottom fade under one line, as a scriptable Chart.js colour.
 *
 * Scriptable rather than a fixed colour because a canvas gradient needs the plot's pixel bounds, which do
 * not exist until layout has run. Chart.js calls this again on every resize, so the fade always spans the
 * current plot height instead of being stretched from the first one.
 */
const areaGradient = (colour) => (context) => {
    const {ctx, chartArea} = context.chart;
    // First paint, before layout: a flat wash stands in, and the real gradient arrives on the next frame.
    if (!chartArea) return withAlpha(colour, 0.2);
    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, withAlpha(colour, 0.45));
    gradient.addColorStop(1, withAlpha(colour, 0));
    return gradient;
};

/**
 * The vertical line under the cursor, tying the tooltip to a point on the x-axis.
 *
 * Chart.js has no built-in crosshair, and this is passed to the one chart that wants it rather than
 * registered globally — `usage.jsx` and `lastAccessed.jsx` share this library and should not inherit it.
 */
const crosshairPlugin = {
    id: 'ingestCrosshair',
    afterDatasetsDraw: (chart) => {
        const active = chart.tooltip?.getActiveElements?.() ?? [];
        if (!active.length) return;
        const {ctx, chartArea} = chart;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(active[0].element.x, chartArea.top);
        ctx.lineTo(active[0].element.x, chartArea.bottom);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
        ctx.stroke();
        ctx.restore();
    },
};

/*
 * Dark-surface chrome, kept in one place so the axes, legend and tooltip cannot drift apart.
 *
 * White rather than the muted grey a chart on a near-black surface would use: this card is the mid grey
 * of CARD_SURFACE (#3a3e3f in graphSkeleton.jsx), and against that a grey tick label all but disappears.
 * The grid keeps its very low opacity, though — it is there to be measured against, not read, and it is
 * already legible on this surface.
 */
const AXIS_TEXT = '#ffffff';
const LEGEND_TEXT = '#ffffff';
const GRID_LINE = 'rgba(255, 255, 255, 0.06)';

/*
 * One colour per series. At module scope because the lines and the totals above them have to agree: a card
 * bordered green over an amber line would be worse than no colour at all.
 *
 * Keyed by label rather than by position, because the series that reach the chart are not always all of them.
 * A tape-only customer has no file counts, so Files Count is dropped before the chart is built (see the
 * rawData.length filter in ingestedDataRetriever) — and off a positional palette Data Size would slide into
 * the vacated slot and be drawn salmon instead of amber. The cards are coloured from the same lookup, so both
 * would have moved together and neither would have looked like a fault.
 */
const SERIES_COLOURS_BY_LABEL = {
    "Media Count": 'rgba(0,126,35,1)',
    /*
     * Salmon, not the green it used to be: Files Count sat between two greens on the cards and its line runs
     * right alongside Media Count, and two greens that close together read as one series. Deliberately not
     * reusing RISING below, which is the same green an up-arrow is drawn in and has to stay that way.
     *
     * Salmon rather than a truer coral (255,127,80) because of the area fill: this line's fill covers most of
     * the panel, and over the card's grey surface a coral wash turns brown. The pinker cast survives being
     * laid over grey at low opacity.
     */
    "Files Count": 'rgba(250,128,114,1)',
    "Data Size": 'rgba(244,191,55,1)',
};

/** Anything the lookup does not name, in order of first appearance. */
const SERIES_COLOURS_FALLBACK = [
    'rgba(238,63,63,1)',
    'rgba(193,89,89,1)',
];

/**
 * The colour for one series.
 *
 * The `Mock ` prefix is stripped so the mock retriever's series — "Mock Files Count" and friends — are painted
 * the same as the real ones they stand in for, rather than dropping through to the fallbacks and making the
 * mock chart a different chart to look at.
 */
const seriesColour = (label, idx) => SERIES_COLOURS_BY_LABEL[String(label ?? '').replace(/^Mock /, '')]
    ?? SERIES_COLOURS_FALLBACK[idx % SERIES_COLOURS_FALLBACK.length];

/** Green for a rise, red for a fall — the arrow beside a total. */
const RISING = 'rgba(56,170,88,1)';
const FALLING = 'rgba(238,63,63,1)';

/*
 * The units the size axis can be drawn in, smallest first.
 *
 * Powers of 1000, matching data_size_iso_units. Do not "correct" these to KiB/MiB/PiB: it would put the axis
 * in a different unit from the total above it and the tooltip beside it, both of which are ISO.
 */
const SIZE_UNITS = [
    {unit: 'B', divisor: 1},
    {unit: 'KB', divisor: 1000},
    {unit: 'MB', divisor: Math.pow(1000, 2)},
    {unit: 'GB', divisor: Math.pow(1000, 3)},
    {unit: 'TB', divisor: Math.pow(1000, 4)},
    {unit: 'PB', divisor: Math.pow(1000, 5)},
    {unit: 'EB', divisor: Math.pow(1000, 6)},
];

/*
 * Step sizes worth drawing an axis in — only the ones a reader counts in. A step of 9 would be whole and
 * still leave the axis reading 0, 9, 18, 27.
 */
const NICE_STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500];

/**
 * The size axis fitted to the data: which unit it is drawn in, where it stops, and how far apart its ticks
 * sit (the last two in bytes, which is what Chart.js is given).
 *
 * The unit is chosen from the peak rather than fixed at PB, which is what this did before. Fixed PB gave one
 * declared unit for every job, but almost every real ingest is a rounding error against a petabyte: a 24.79 GB
 * job was drawn as a flat line along the bottom of a 0–10 PB scale, which says "nothing arrived" about data
 * that is plainly there. The trade being accepted in return is that the axis now reads GB for one selection
 * and TB for another — so the unit is named in the axis title, and every tick carries it too.
 *
 * Bounds are still given explicitly rather than left to Chart.js, because its own choice is free to land on
 * fractional steps. The step is the smallest nice size keeping the axis under about ten ticks, and the top is
 * rounded up to a whole number of those steps: a 24.79 GB peak gives 0, 5, 10 … 25 GB, and an 87 PB peak
 * still gives 0, 10, 20 … 90 PB, the design's axis.
 *
 * @param peakBytes - the largest figure that will be plotted on this axis.
 */
const sizeAxisScale = (peakBytes) => {
    const peak = Number(peakBytes) || 0;
    /*
     * The largest unit the peak is at least one of, so the axis top is a small number with a big unit rather
     * than the reverse. Bytes when there is nothing to plot at all — an empty axis labelled EB would be a
     * stranger thing to show than one labelled B.
     */
    const scale = SIZE_UNITS.filter(candidate => peak / candidate.divisor >= 1).pop() ?? SIZE_UNITS[0];
    // At least 1, so a peak below a whole unit still gets an axis rather than one collapsed to zero.
    const wanted = Math.max(1, Math.ceil(peak / scale.divisor));
    const step = NICE_STEPS.find(candidate => wanted / candidate <= 10) ?? NICE_STEPS[NICE_STEPS.length - 1];

    return {
        unit: scale.unit,
        max: Math.ceil(wanted / step) * step * scale.divisor,
        stepSize: step * scale.divisor,
        /**
         * One tick, in the unit chosen above.
         *
         * Rounded rather than given decimals, because the axis is laid out in whole steps of this unit so
         * every tick is already a whole one and this only has to say so.
         *
         * Only for ticks. A single figure — a total — needs the unit that suits its own size independently of
         * whatever the axis settled on, and formatData is still what does that.
         */
        tickLabel: (bytes) =>
            `${Math.round((Number(bytes) || 0) / scale.divisor).toLocaleString()} ${scale.unit}`,
    };
};

/**
 * Mock data retriever function for the WaterfallGraph component. The function sets loading to true, simulates retrieving data after a delay, and then sets the data and loading state to false.
 * @param setRawData - Function to set the raw data state.
 * @param setLoading - Function to set the loading state.
 * @param setAllSeries - Function to set the list of all series.
 * @param setSeriesToShow - Function to set the series to show state.
 */
const mockDataRetriever = (setRawData, setLoading, setAllSeries, setSeriesToShow, _jobIds, _cid, options = {}) => {
    // Simulate fetching data
    const mockData = [
        {
            leftAxis: true,
            label: "Mock Media Count",
            ui_class_to_format_to: "units",
            rawData: [
                {date: "2021-04-08", value: 30},
                {date: "2021-04-09", value: 32},
                {date: "2021-04-10", value: 31},
                {date: "2021-04-11", value: 19},
                {date: "2022-10-08", value: 30},
                {date: "2022-10-09", value: 32},
                {date: "2022-10-10", value: 31},
                {date: "2022-10-11", value: 19},
            ]
        },
        {
            leftAxis: true,
            label: "Mock Files Count",
            ui_class_to_format_to: "units",
            rawData: [
                {date: "2021-04-08", value: 882},
                {date: "2021-04-09", value: 1210},
                {date: "2021-04-10", value: 1943},
                {date: "2021-04-11", value: 4509},
                {date: "2022-10-08", value: 882},
                {date: "2022-10-09", value: 1210},
                {date: "2022-10-10", value: 1943},
                {date: "2022-10-11", value: 4509},
            ]
        },
        {
            leftAxis: false,
            label: "Mock Data Size",
            ui_class_to_format_to: "data_size_iso_units",
            rawData: [
                {date: "2021-04-08", value: 42383006746},
                {date: "2021-04-09", value: 45623421768},
                {date: "2021-04-10", value: 23401044706},
                {date: "2021-04-11", value: 75395961993},
                {date: "2022-10-08", value: 42383006746},
                {date: "2022-10-09", value: 45623421768},
                {date: "2022-10-10", value: 23401044706},
                {date: "2022-10-11", value: 75395961993},
            ]
        },
    ];

    const second = 1000;
    const mockDelay = 5 * second;
    setLoading(true);
    const handle = setTimeout(() => {
        if (options.signal?.aborted) return;
        setRawData(mockData);
        setAllSeries(mockData.map(series => series.label));
        setSeriesToShow(mockData.map(series => series.label));
        setLoading(false);
    }, mockDelay);

    if (options.signal) {
        options.signal.addEventListener('abort', () => clearTimeout(handle), { once: true });
    }
};

const ingestedDataRetriever = (setRawData, setLoading, setAllSeries, setSeriesToShow, jobIds, cid, options = {}) => {
    setLoading(true);

    fetchIngestedTimelineData(jobIds, cid, options)
        .then(response => {
            if (!['1.2'].includes(response?.version)) throw new Error(`Unsupported data version returned by fetchIngestedTimelineData function: ${response?.version}`);
            const tempRawData = [
                {
                    leftAxis: true,
                    // "Media", as the rest of the portal says, rather than "Tapes" as the design drew it. The
                    // count is the same one either way — ArkBridge's `tapes` per ingest date — and this string
                    // is the legend entry, the tooltip row and the heading on its total, nothing more. It is
                    // also the key the palette is looked up by, so it has to match SERIES_COLOURS_BY_LABEL.
                    label: "Media Count",
                    ui_class_to_format_to: "units",
                    rawData: []
                },
                {
                    leftAxis: true,
                    label: "Files Count",
                    ui_class_to_format_to: "units",
                    rawData: []
                },
                {
                    leftAxis: false,
                    label: "Data Size",
                    ui_class_to_format_to: "data_size_iso_units",
                    rawData: []
                },
            ];

            if (!response?.result || Object.keys(response.result).length === 0) {
                setRawData([]);
                setAllSeries([]);
                setSeriesToShow([]);
            } else {
                Object.values(response?.result)?.forEach(companyDetails => {
                    companyDetails?.data?.forEach(dataPoint => {
                        if (dataPoint) {
                            const ingested_date = dataPoint?.ingest_date ?? "1999-01-01";
                            if (dataPoint?.bytes) tempRawData[2].rawData?.push({date: ingested_date, value: dataPoint?.bytes});
                            if (dataPoint?.files) tempRawData[1].rawData?.push({date: ingested_date, value: dataPoint?.files});
                            if (dataPoint?.tapes) tempRawData[0].rawData?.push({date: ingested_date, value: dataPoint?.tapes});
                        }
                    });
                });
                /*
                 * A company with nothing ingested still comes back as a result — `rep_ingest` answers
                 * {"result":{"<cid>":{"count":0}},"message":"NO entries found"}, which has a key and so
                 * passes the check above, but carries no `data` array for the loop to read. That left
                 * `rawData` holding three empty series, and the render tests `rawData.length` rather than
                 * the points inside it, so three-greater-than-zero drew the chart branch with nothing to
                 * plot: a blank box, no "No Data". Decide it on whether any series actually got a point.
                 */
                const populated = tempRawData.filter(series => series.rawData.length > 0);
                setRawData(populated.length > 0 ? tempRawData : []);
                setAllSeries(populated.map(series => series.label));
                setSeriesToShow(populated.map(series => series.label));
            }
            setLoading(false);
        })
        .catch(err => {
            console.log("Error in fetchIngestedTimelineData ", err);

            setRawData([]);
            setAllSeries([]);
            setSeriesToShow([]);
            setLoading(false);
        });
};

const variantDataRetrieverMap = {
    defValue: mockDataRetriever,
    ingestedData: ingestedDataRetriever,
};

// Ensure month abbreviation is capitalized in the usual form (for example, "Jan", "Feb", "Mar", …)
const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * @param variant - which retriever and which figures; see the variant map above.
 * @param heading - the card's heading, when the caller wants it drawn under the period tabs rather than
 *   above them by the frame. Absent for a caller that lets widgetsGrid draw it, and nothing is rendered in
 *   its place — see home.jsx's headingInside.
 */
export const WaterfallGraph = ({variant, heading, onDataState}) => {
    const classes = useStyles();
    const [rawData, setRawData] = useState(null);
    const [loading, setLoading] = useState(true);
    /*
     * Tells the dashboard whether there is anything to plot, so it can give this card's space to the Media
     * Metrics card when there is not. The same test the render uses for 'No Data', so the two cannot
     * disagree; 'loading' until an answer is in, so the layout does not jump while it is fetched.
     */
    useEffect(() => {
        onDataState?.(loading ? 'loading' : (rawData?.length > 0 ? 'data' : 'empty'));
    }, [loading, rawData, onDataState]);
    /*
     * The scope the timeline covers — the jobs ticked in the header, or every job the selector lists.
     *
     * Taken from the shared hook rather than straight off the context so this card and the Media Metrics
     * card cannot disagree about what 'All Jobs' means; see useDashboardJobScope. Unscoped, `rep_ingest`
     * returns the per-job rows of `xag_ingest` and the company-wide roll-up rows together, and this chart
     * accumulates a running total — so the same ingest was counted twice as the bars climbed.
     */
    const {jobIds, cid, jobIdsKey, waiting, nothingOnboarded} = useDashboardJobScope();

    const [periodToShow, setPeriodToShow] = useState('day');
    const [allSeries, setAllSeries] = useState([]);
    const [seriesToShow, setSeriesToShow] = useState([]);

    const [processedData, setProcessedData] = useState(null);

    const [areaGraphData, setAreaGraphData] = useState(null);
    const [areaGraphOptions, setAreaGraphOptions] = useState(null);

    useEffect(() => {
        // Waiting on the header's job list: the scope is not known yet, so the chart stays on its own
        // spinner rather than drawing bars it would have to redraw.
        if (waiting) {
            setLoading(true);
            return;
        }
        /*
         * Nothing onboarded, so nothing to plot. Reported as no data rather than by dropping the job scope,
         * which would put every company in ArkBridge on a chart the portal has no jobs for.
         *
         * `rawData` is emptied as an array, not left null: the render below reads `rawData.length`.
         */
        if (nothingOnboarded) {
            setRawData([]);
            setAllSeries([]);
            setSeriesToShow([]);
            setLoading(false);
            return;
        }
        let isMounted = true;
        const controller = new AbortController();
        const onAbort = () => {
            if (isMounted) setLoading(false);
        };
        controller.signal.addEventListener('abort', onAbort, {once: true});

        let dataRetrieverFn = variantDataRetrieverMap[variant] ?? variantDataRetrieverMap.defValue;
        /*
         * `background` keeps this out of the full-window wash the axios interceptor raises (see
         * utils/loadingWash.js). This widget already says it is loading, in its own box and only over its
         * own box: `loading` above puts an AreaChartSkeleton where the chart goes. The wash on top of that
         * added nothing except a sheet over the whole portal — sidebar included — that swallowed every
         * click until the slowest widget on the dashboard had answered. Nobody can navigate away from a
         * dashboard while it loads, which is what was reported.
         */
        dataRetrieverFn(setRawData, setLoading, setAllSeries, setSeriesToShow, jobIds, cid, {signal: controller.signal, background: true});

        return () => {
            isMounted = false;
            controller.abort();
            controller.signal.removeEventListener('abort', onAbort);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant, jobIdsKey, cid, waiting, nothingOnboarded]);

    const convertDateToPeriod = useCallback((dateStr) => {
        const formattedDate = formatData(dateStr, "date")
        const [dayStr, monAbbr, yearStr] = formattedDate.split("/");
        if (!dayStr || !monAbbr || !yearStr) {
            throw new Error(`Invalid date format—expected "DD/MMM/YYYY": received "${formattedDate}"`);
        }

        // Find the zero‐based index of the month
        const monthIndex = monthNames.indexOf(
            // normalize for example "mar" → "Mar"
            monAbbr.charAt(0).toUpperCase() + monAbbr.slice(1).toLowerCase()
        );
        if (monthIndex < 0) {
            throw new Error(`Unrecognized month "${monAbbr}". Use a three-letter English abbreviation.`);
        }
        const yearShort = yearStr.slice(2); // for example, "2021" → "21"
        const monthNum = monthIndex + 1;     // 1..12

        switch (periodToShow) {
            case "day":
                return dateStr;

            case "month":
                // For example, "Mar '21"
                return `${monthNames[monthIndex]} '${yearShort}`;

            case "quarter":
                // Q1 = Jan–Mar, Q2 = Apr–Jun, and so on
                const quarter = Math.ceil(monthNum / 3);
                return `Q${quarter} '${yearShort}`;

            case "year":
                // Return full year, for example, "2021"
                return yearStr;

            default:
                throw new Error(`Unsupported period "${periodToShow}". Use "day", "month", "quarter", or "year".`);
        }
    }, [periodToShow]);

    /**
     * Converts a raw value to a formatted value based on the label.
     * This function is a placeholder and can be customized to handle different data types or formats.
     * @param {number} rawValue - The raw value to be converted.
     * @param {string} label - The label associated with the raw value, used to determine the conversion logic.
     */
    const convertRawValue = useCallback((rawValue, label) => {
        return rawValue;
    }, [])

    const customComparator = useMemo(() => {
        // Return a comparator function that depends on periodToShow
        switch (periodToShow) {
            case "day":
                return (a, b) => a.localeCompare(b);

            case "month":
                // For example, "Mar '21"
                return (a, b) => {
                    if (a === b) return 0;

                    const [monA, yearA] = a.split(" '");
                    const [monB, yearB] = b.split(" '");

                    const yearNumA = parseInt(yearA, 10);
                    const yearNumB = parseInt(yearB, 10);

                    if (yearNumA !== yearNumB) return yearNumA - yearNumB;

                    const monthIndexA = monthNames.indexOf(monA);
                    const monthIndexB = monthNames.indexOf(monB);

                    return monthIndexA - monthIndexB;
                };

            case "quarter":
                // Q1 = Jan–Mar, Q2 = Apr–Jun, and so on
                return (a, b) => {
                    if (a === b) return 0;

                    const [qtrA, yearA] = a.split(" '");
                    const [qtrB, yearB] = b.split(" '");

                    const yearNumA = parseInt(yearA, 10);
                    const yearNumB = parseInt(yearB, 10);

                    if (yearNumA !== yearNumB) return yearNumA - yearNumB;

                    const quarterNumA = parseInt(qtrA.slice(1), 10); // Remove "Q" prefix and convert to number
                    const quarterNumB = parseInt(qtrB.slice(1), 10);

                    return quarterNumA - quarterNumB;
                };

            case "year":
                // Return full year, for example, "2021"
                return (a, b) => a.localeCompare(b);

            default:
                throw new Error(`Unsupported period "${periodToShow}". Use "day", "month", "quarter", or "year".`);
        }
    }, [periodToShow]);

    useEffect(() => {
        if (rawData === null) return;

        setLoading(true);
        const processedSeries = rawData?.filter(series => seriesToShow.includes(series.label)).map(series => {
            const processed = {
                leftAxis: series.leftAxis,
                label: series.label,
                ui_class_to_format_to: series.ui_class_to_format_to,
                data: {}
            };

            series.rawData.forEach(dataPoint => {
                const dateKeyStr = convertDateToPeriod(dataPoint.date);
                if (dateKeyStr in processed.data) {
                    processed.data[dateKeyStr].amount += convertRawValue(dataPoint.value, series.label);
                } else {
                    processed.data[dateKeyStr] = {
                        start: 0,
                        amount: convertRawValue(dataPoint.value, series.label)
                    };
                }
            });

            let runningTotal = 0;
            Object.keys(processed.data).sort(customComparator).forEach(dateKey => {
                const dataPoint = processed.data[dateKey];
                dataPoint.start = runningTotal;
                runningTotal += dataPoint.amount;
            });

            return processed;
        });

        setProcessedData(processedSeries);
        setLoading(false);
    }, [rawData, convertDateToPeriod, allSeries, seriesToShow]);

    /**
     * The headline figure per series, and its movement between the two most recent periods.
     *
     * `total` is the sum of every row, which is the same number whichever period is selected — the toggle
     * only regroups the rows — and it is where the line ends on the chart. So it is the one figure on this
     * card that is not open to interpretation.
     *
     * `change` is, and it is worth being precise about what it means, because the payload defines no
     * baseline: it compares the amount ingested in the most recent period against the amount ingested in
     * the period before it, at whichever granularity is selected. Switching Quarter → Year therefore moves
     * the percentage while leaving the total alone, which is correct — a year-on-year change is a different
     * question from a quarter-on-quarter one.
     *
     * Two cases where there is honestly nothing to show, and both return null rather than 0: a series with
     * only one period of activity has nothing to compare against, and one whose earlier period was zero
     * would divide by it. Note that periods with no rows at all are absent from `data`, so "the period
     * before" means the previous period that saw any ingest, not necessarily the adjacent one on the axis.
     */
    const seriesTotals = useMemo(() => {
        if (!processedData) return [];

        return processedData.map(series => {
            const periods = Object.keys(series.data).sort(customComparator);
            const latest = periods.length ? series.data[periods[periods.length - 1]] : null;
            const previous = periods.length > 1 ? series.data[periods[periods.length - 2]] : null;

            return {
                label: series.label,
                format: series.ui_class_to_format_to,
                // start + amount on the last period, i.e. the running total once everything is in.
                total: latest ? latest.start + latest.amount : 0,
                change: previous && previous.amount > 0
                    ? ((latest.amount - previous.amount) / previous.amount) * 100
                    : null,
                latestPeriod: periods[periods.length - 1],
                previousPeriod: previous ? periods[periods.length - 2] : null,
            };
        });
    }, [processedData, customComparator]);

    useEffect(() => {
        if (!processedData) return;

        const hasRightAxisSeries = processedData.some((series) => !series.leftAxis && Object.values(series.data).some((point) => point.amount != null && point.amount !== 0));
        const hasLeftAxisSeries = processedData.some((series) => series.leftAxis && Object.values(series.data).some((point) => point.amount != null && point.amount !== 0));

        let uniqueHorizontalLabels = new Set();
        processedData.forEach(series => Object.keys(series.data).forEach(dateKey => uniqueHorizontalLabels.add(dateKey)));
        uniqueHorizontalLabels = Array.from(uniqueHorizontalLabels).sort(customComparator);

        const seriesLabelFormattingMap = {};
        processedData.forEach(series => seriesLabelFormattingMap[series.label] = series.ui_class_to_format_to);

        // The highest running total the size axis has to hold, which is what sets its top and its step.
        const rightAxisPeak = Math.max(0, ...processedData
            .filter(series => !series.leftAxis)
            .flatMap(series => Object.values(series.data).map(point => (point.start ?? 0) + (point.amount ?? 0))));
        const sizeAxis = sizeAxisScale(rightAxisPeak);

        /*
         * The running total at each period — the same figure the waterfall's bar tops already traced, now
         * drawn as the line itself. The offset datasets that used to float those bars are gone with it.
         *
         * Carried forward across periods a series has no row for, rather than read as zero: this is a
         * cumulative total, so a quarter with no ingest holds it level. Plotting zero would drop the line to
         * the axis and back, inventing a collapse and a re-ingest that never happened. Zero before the first
         * row is right, though — nothing had been ingested yet.
         */
        const cumulativeSeries = (series) => {
            let carried = 0;
            return uniqueHorizontalLabels.map(dateKey => {
                const point = series.data[dateKey];
                if (point) carried = point.start + point.amount;
                return carried;
            });
        };

        const data = {
            labels: uniqueHorizontalLabels,
            datasets: processedData.map((series, idx) => {
                const colour = seriesColour(series.label, idx);
                return {
                    label: series.label,
                    data: cumulativeSeries(series),
                    yAxisID: series.leftAxis ? 'y' : 'y1',
                    borderColor: colour,
                    backgroundColor: areaGradient(colour),
                    fill: 'origin',
                    // Curved rather than jointed: the periods are buckets of one continuous ingest, not
                    // separate readings, so a spline reads the way the process behind it actually behaves.
                    tension: 0.4,
                    borderWidth: 2,
                    // No marker per period — at day granularity there can be hundreds — but a clear one
                    // under the cursor, so it is obvious which point the crosshair and tooltip describe.
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    pointHoverBackgroundColor: colour,
                    pointHoverBorderColor: '#ffffff',
                    pointHoverBorderWidth: 2,
                };
            }),
        };

        setAreaGraphData(data);

        const options = {
            maintainAspectRatio: false, // <— let Chart.js fill its container
            responsive: true,
            /*
             * Hovering anywhere in a column reports every series at that period, so the tooltip answers
             * "what did this quarter look like" rather than "which line is nearest the cursor". Set here as
             * well as on the tooltip because the crosshair reads its position from the active elements, and
             * those are chosen by this interaction mode.
             */
            interaction: {mode: 'index', intersect: false},
            plugins: {
                /*
                 * The legend is the series switch now that the left-hand rail is gone — Chart.js toggles a
                 * dataset when its legend entry is clicked, which is the same capability in the place the
                 * design puts it. Each series carries its own running total, so hiding one cannot disturb
                 * what the others report.
                 */
                legend: {
                    position: 'bottom',
                    /*
                     * Chart.js gives the legend no cursor of its own, so the one control on this card that
                     * does something on click looked like a caption. The canvas is a single element — there
                     * is no legend node to put a CSS cursor on — so the cursor has to be swapped as the
                     * pointer enters and leaves an entry.
                     */
                    onHover: (_event, _legendItem, legend) => {
                        legend.chart.canvas.style.cursor = 'pointer';
                    },
                    onLeave: (_event, _legendItem, legend) => {
                        legend.chart.canvas.style.cursor = 'default';
                    },
                    labels: {
                        usePointStyle: true,
                        pointStyle: 'circle',
                        boxWidth: 8,
                        boxHeight: 8,
                        padding: 16,
                        color: LEGEND_TEXT,
                        font: {size: 11},
                        /*
                         * Filled dot while the series is on, hollow ring while it is off, so the marker
                         * reads as a radio button rather than a colour swatch — which of the three is
                         * showing is then legible at a glance instead of only from the strikethrough
                         * Chart.js puts through the switched-off label.
                         *
                         * Written out rather than left to the default because the default takes its fill
                         * from the dataset's backgroundColor, and ours is the area gradient: it is built in
                         * plot coordinates and fades to fully transparent at the bottom of the chart, which
                         * is exactly where the legend sits. Every dot came out empty. borderColor is the
                         * flat palette colour, so that is what the marker is drawn from.
                         */
                        generateLabels: (chart) => chart.data.datasets.map((dataset, index) => {
                            const shown = chart.isDatasetVisible(index);
                            return {
                                text: dataset.label,
                                fillStyle: shown ? dataset.borderColor : 'transparent',
                                strokeStyle: dataset.borderColor,
                                lineWidth: 2,
                                pointStyle: 'circle',
                                // Per item, not from labels.color: the legend paints each entry with its
                                // own fontColor, and copying labels.color into it is one of the things the
                                // default generateLabels does. Overriding it without this left the text
                                // drawn in whatever fill
                                // the canvas held last — black on this surface. It colours the
                                // strikethrough on a switched-off entry too.
                                fontColor: LEGEND_TEXT,
                                // Drives the strikethrough, and the click handler reads datasetIndex.
                                hidden: !shown,
                                datasetIndex: index,
                            };
                        }),
                    }
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    backgroundColor: 'rgba(17, 22, 28, 0.95)',
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                    borderWidth: 1,
                    titleColor: '#ffffff',
                    bodyColor: LEGEND_TEXT,
                    padding: 10,
                    usePointStyle: true,
                    callbacks: {
                        label: (ctx) => {
                            const rawValue = ctx.parsed.y;
                            return `${ctx.dataset.label}: ${formatData(rawValue, seriesLabelFormattingMap[ctx.dataset.label])}`;
                        }
                    }
                },
            },
            scales: {
                x: {
                    type: 'category',
                    grid: {color: GRID_LINE},
                    border: {color: 'rgba(255, 255, 255, 0.12)'},
                    ticks: {
                        autoSkip: false,
                        maxRotation: periodToShow === 'day' ? 90 : 0,
                        color: AXIS_TEXT,
                        font: {size: 10},
                    },
                },
                /*
                 * `stacked` is gone from both axes. It was what made the waterfall work — the offset dataset
                 * lifted the visible one — and on lines it would silently add each series to the one below
                 * it, so Files Count would have been drawn at Files + Media.
                 */
                y: {
                    display: hasLeftAxisSeries,
                    type: 'linear',
                    position: 'left',
                    beginAtZero: true,
                    grid: {color: GRID_LINE},
                    border: {display: false},
                    title: {display: true, text: 'Count', color: AXIS_TEXT},
                    ticks: {
                        color: AXIS_TEXT,
                        font: {size: 10},
                        // Thousands separators: these run to nine figures, and an unbroken run of digits is
                        // not readable at tick size.
                        callback: (value) => Number(value).toLocaleString(),
                    },
                },
                y1: {
                    display: hasRightAxisSeries,
                    type: 'linear',
                    position: 'right',
                    beginAtZero: true,
                    // Whole steps of the unit sizeAxisScale settled on, not Chart.js's own fit. min is stated
                    // alongside max because a scale given only one end is still free to move the other.
                    min: 0,
                    max: sizeAxis.max,
                    grid: {drawOnChartArea: false},
                    border: {display: false},
                    // Names the unit its ticks are in, as the design has it — and now that the unit follows the
                    // data, this is where a reader finds out which one they are looking at.
                    title: {display: true, text: `Size (${sizeAxis.unit})`, color: AXIS_TEXT},
                    ticks: {
                        color: AXIS_TEXT,
                        font: {size: 10},
                        stepSize: sizeAxis.stepSize,
                        // Whole units, since the step is a whole number of them.
                        callback: (value) => sizeAxis.tickLabel(value),
                    }
                },
            },
        };

        setAreaGraphOptions(options);

    }, [processedData]);

    const periodSelector = () => {
        return (
            <Grid container direction="row" justifyContent="flex-start">
                {/* Grows to the width the header row gives this column — which ends where the totals begin —
                    rather than sitting at the width of its four labels. The strip and the buttons in it each
                    have to be told to fill, or the box would stretch and leave the tabs bunched at its left. */}
                <Grid item sx={{flexGrow: 1, minWidth: 0}}>
                    <Paper elevation={1} sx={{
                        border: '1px solid #FFC540',
                        display: 'flex',
                        background: 'transparent',
                        flexWrap: 'wrap',
                        padding: 0.25,
                        width: '100%',
                        boxSizing: 'border-box',
                    }}>
                        <ToggleButtonGroup
                            color="primary"
                            value={periodToShow}
                            exclusive
                            onChange={e => setPeriodToShow(e.target.value)}
                            aria-label="Period Selector"
                            sx={{width: '100%'}}
                        >
                            {['day', 'month', 'quarter', 'year'].map((val) => (
                                <ToggleButton
                                    key={val}
                                    value={val}
                                    sx={{
                                        lineHeight: 1.1,
                                        /*
                                         * The shared ToggleButton is a fixed 116px wide and 35px tall, so
                                         * four of them take some 480px — most of this card — and the totals
                                         * were pushed onto a row of their own. Sized to its own label
                                         * instead, which is how the design draws the four tabs.
                                         *
                                         * !important throughout because those dimensions come from the
                                         * styled() wrapper in uiUtils under a bare `&`, and this has to
                                         * outrank it without touching the shared component: the same button
                                         * sizes tab strips elsewhere in the portal.
                                         */
                                        width: 'auto !important',
                                        height: 'auto !important',
                                        minWidth: 'auto !important',
                                        minHeight: 'auto !important',
                                        // Equal shares of the stretched strip. The padding below is still what
                                        // sets the minimum, so a narrow card falls back to label-sized tabs
                                        // rather than four tabs squeezed past legibility.
                                        flexGrow: 1,
                                        px: 1.25,
                                        py: 0.4,
                                        textTransform: 'none',
                                        display: 'inline-flex !important',
                                        fontSize: '0.75rem !important',
                                    }}
                                >
                                    {val.charAt(0).toUpperCase() + val.slice(1)}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Paper>
                </Grid>
            </Grid>);
    };

    /**
     * The three headline totals from the design — Total Media Count, Total Files Count, Total Data Size.
     *
     * Alongside the period selector rather than in full-height cards above the chart as the design draws
     * them: the dashboard has to fit one screen without scrolling (see the note in home.jsx and the height
     * cap in widgetsGrid.jsx), and a third row inside this card would come straight out of the plot. Same
     * three figures, same order, bordered in each series' own colour so a total and its line are readable
     * as the same thing.
     */
    const totalsStrip = () => (
        // Takes the whole row to the right of the tabs and divides it between the three, so they reach the
        // card's edge at any width instead of sitting at their minimum with dead space beside them.
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, flexGrow: 1, justifyContent: 'flex-end'}}>
            {seriesTotals.map((stat, idx) => {
                const rising = stat.change >= 0;
                return (
                    <Paper
                        key={stat.label}
                        elevation={0}
                        sx={{
                            background: 'transparent',
                            border: `1px solid ${seriesColour(stat.label, idx)}`,
                            borderRadius: 1,
                            px: 1.25,
                            /*
                             * Close to the text, top and bottom. These boxes are the tallest thing in the
                             * header row, so their height is the row's height and the border was landing flush
                             * against the top of the plot. Shallow padding here, and the space it frees is
                             * given back below the row as a gap — see the header row's pb — so the box closes
                             * clear of the chart rather than the chart moving up to meet it. 2px rather than 0
                             * so the border does not touch the glyphs.
                             */
                            py: 0.25,
                            /*
                             * Equal shares of the space the strip is given, and a floor under each, so the
                             * three read as a set rather than three widths — 5,420,000 would otherwise sit
                             * in twice the box that 15,230 does. The floor is also what makes them wrap
                             * below the tabs on a narrow card instead of squeezing to nothing.
                             */
                            flexGrow: 1,
                            flexBasis: 0,
                            minWidth: '7.5em',
                        }}
                    >
                        <Typography sx={{fontSize: '0.7rem', color: LEGEND_TEXT, whiteSpace: 'nowrap'}}>
                            Total {stat.label}
                        </Typography>
                        <Typography sx={{
                            fontSize: '1.3rem',
                            fontWeight: 700,
                            color: '#ffffff',
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                        }}>
                            {/* The unit that suits this one figure, chosen independently of the axis. The two
                                now usually agree — the axis follows its data too, see sizeAxisScale — but they
                                are allowed to differ, because rounding a total up to the axis's step would
                                report 24.79 GB as 25 GB. A headline figure has to be exact and legible on its
                                own, so it keeps formatData. */}
                            {stat.format === 'data_size_iso_units'
                                ? formatData(stat.total, stat.format)
                                : Number(stat.total).toLocaleString()}
                        </Typography>
                        {/* Absent, rather than shown as 0%, when there is no earlier period to compare
                            against — see seriesTotals. The tooltip names both periods, because what the
                            figure compares moves with the Day/Month/Quarter/Year selection and an
                            unexplained arrow on a dashboard invites the wrong reading. inline-block so the
                            hover target, and the help cursor with it, is the percentage rather than the full
                            width of the box. */}
                        {stat.change !== null &&
                            <MuiTooltip title={`Ingested in ${stat.latestPeriod} against ${stat.previousPeriod}`}>
                                <Typography sx={{
                                    fontSize: '0.7rem',
                                    color: rising ? RISING : FALLING,
                                    whiteSpace: 'nowrap',
                                    cursor: 'help',
                                    display: 'inline-block',
                                }}>
                                    {rising ? '↑ +' : '↓ '}{stat.change.toFixed(1)}%
                                </Typography>
                            </MuiTooltip>}
                    </Paper>
                );
            })}
        </Box>
    );

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <Box sx={{
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                // Tops flush, not centres: the totals are taller than the tab strip, and centring them left
                // the strip floating in the middle of the row instead of starting at the same line.
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                // Separates the totals from the plot. Exactly what the boxes gave up in padding, so the
                // header costs the plot no more height than before — the gap sits inside the same budget.
                pb: 1,
            }}>
                {/* Tabs, then the heading beneath them, with the totals alongside both — the design's
                    arrangement. The heading is drawn here rather than by widgetsGrid because the frame
                    always draws its title above the widget, and nothing above the tabs is wanted. */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                    /*
                     * Takes the width left over once the totals have theirs, so the tab strip reaches across
                     * to where they start instead of stopping at the width of its own labels. The column's
                     * base size is still its content, so the strip can never be squeezed narrower than four
                     * readable tabs — it only grows into slack that would otherwise sit empty.
                     */
                    flexGrow: 1,
                    minWidth: 0,
                }}>
                    {periodSelector()}
                    {/* 16px/20px, which is what Media Metrics sets its own title to and what widgetsGrid
                        draws the other widgets' titles at, so every heading on the dashboard reads as one
                        system. It was 18px to reach the width of the tab strip above it; matching the
                        neighbours won that argument, so the heading now stops a little short of the strip. */}
                    {heading ?
                        <Typography
                            align="left"
                            className={classes.mainHeading}
                            style={{
                                fontWeight: 700,
                                fontSize: '16px',
                                lineHeight: '20px',
                                whiteSpace: 'nowrap',
                                // Indented by the strip's 1px border plus its 2px of padding, so the D of
                                // Data starts where the first tab's box does rather than three pixels to the
                                // left of it. Change this if that padding changes.
                                paddingLeft: '3px',
                            }}
                        >{heading}</Typography> :
                        <></>}
                </Box>
                {/* The strip's geometry is held while the figures are in flight, not left empty.
                    Both columns of this row grow, so an absent strip gave the whole width to the tabs and
                    the four of them stretched across the card — then snapped back to a third of it when the
                    totals landed, taking the heading and the plot with them. See TotalsStripSkeleton. */}
                {loading ?
                    <TotalsStripSkeleton/> :
                    seriesTotals.length > 0 && totalsStrip()}
            </Box>
            {/* A chart-shaped placeholder rather than a spinner in an empty box: the card keeps its real
                regions — plot, axis, legend — so nothing shifts when the figures land. Area-shaped, because
                that is what this card draws: a Line with Filler under it, not bars. `showSeriesRail` is off
                because the series switch now lives in the legend under the plot, so there is no left-hand
                column whose width has to be reserved. See graphSkeleton.jsx. */}
            {loading ?
                <AreaChartSkeleton showSeriesRail={false}/> :
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        width: '100%',
                        height: '100%'
                    }}
                >
                    {rawData.length > 0 ? (
                        <Box
                            sx={{
                                flexGrow: 1,
                                height: '100%',
                                width: '100%',
                                minWidth: 0,
                                minHeight: 0
                            }}
                        >
                            {areaGraphData && areaGraphOptions ?
                                <Line
                                    data={areaGraphData}
                                    options={areaGraphOptions}
                                    plugins={[crosshairPlugin]}
                                /> :
                                <></>
                            }
                        </Box>) : (
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                            }}>
                            <Typography variant="h5" component="div" sx={{fontSize: 14}} color="white">No Data </Typography>
                        </Box>
                    )}
                </Box>
            }
        </Box>);
};